package com.sweetlime.dungeonamigo.presentation.features.character

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.sweetlime.dungeonamigo.model.Character
import com.sweetlime.dungeonamigo.model.CharacterCalculator
import com.sweetlime.dungeonamigo.util.AvatarMapper
import com.sweetlime.dungeonamigo.model.InventoryItem
import com.sweetlime.dungeonamigo.model.SpellSlotLevel
import com.sweetlime.dungeonamigo.repository.AuthRepository
import com.sweetlime.dungeonamigo.repository.CharacterRepository
import com.sweetlime.dungeonamigo.repository.UserRepository
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.launch

sealed class CharacterListState {
    object Loading : CharacterListState()
    data class Ready(val characters: List<Character>) : CharacterListState()
    data class Error(val message: String) : CharacterListState()
    object ShowUpgradePrompt : CharacterListState()
}

enum class CreationStep { RACE, CLASS, ABILITIES, DETAILS, REVIEW }

class CharacterViewModel(
    private val characterRepo: CharacterRepository,
    private val authRepository: AuthRepository,
    private val userRepository: UserRepository,
) : ViewModel() {

    private val _characterListState = MutableStateFlow<CharacterListState>(CharacterListState.Loading)
    val characterListState: StateFlow<CharacterListState> = _characterListState.asStateFlow()

    private val _selectedCharacter = MutableStateFlow<Character?>(null)
    val selectedCharacter: StateFlow<Character?> = _selectedCharacter.asStateFlow()

    private val _creationStep = MutableStateFlow(CreationStep.RACE)
    val creationStep: StateFlow<CreationStep> = _creationStep.asStateFlow()

    private val _creationDraft = MutableStateFlow(Character())
    val creationDraft: StateFlow<Character> = _creationDraft.asStateFlow()

    private val _isCreating = MutableStateFlow(false)
    val isCreating: StateFlow<Boolean> = _isCreating.asStateFlow()

    private val _userTier = MutableStateFlow("free")
    val userTier: StateFlow<String> = _userTier.asStateFlow()

    init {
        val userId = authRepository.currentUserId()
        if (userId != null) {
            viewModelScope.launch {
                characterRepo.getCharacters(userId)
                    .catch { _characterListState.value = CharacterListState.Error(it.message ?: "Failed to load") }
                    .collect { chars -> _characterListState.value = CharacterListState.Ready(chars) }
            }
            viewModelScope.launch {
                _userTier.value = userRepository.getUserProfile(userId)?.tier ?: "free"
            }
        } else {
            _characterListState.value = CharacterListState.Ready(emptyList())
        }
    }

    fun loadCharacter(characterId: String) {
        val userId = authRepository.currentUserId() ?: return
        viewModelScope.launch {
            _selectedCharacter.value = characterRepo.getCharacter(userId, characterId)
        }
    }

    // ── Creation wizard ─────────────────────────────────────────────────────

    fun updateDraftRace(race: String, subrace: String) {
        val draft = _creationDraft.value.copy(race = race, subrace = subrace)
        _creationDraft.value = draft.copy(avatarId = AvatarMapper.avatarId(draft.race, draft.characterClass))
    }

    fun updateDraftClass(characterClass: String) {
        val hitDie = CharacterCalculator.CLASS_HIT_DICE[characterClass] ?: "d8"
        val draft = _creationDraft.value.copy(characterClass = characterClass, hitDice = "1$hitDie")
        _creationDraft.value = draft.copy(avatarId = AvatarMapper.avatarId(draft.race, draft.characterClass))
    }

    fun updateDraftAvatarVariant(variant: String) {
        val draft = _creationDraft.value
        val newId = AvatarMapper.avatarIdWithVariant(draft.race, draft.characterClass, variant)
        _creationDraft.value = draft.copy(avatarId = newId)
    }

    fun updateDraftAbilities(str: Int, dex: Int, con: Int, int: Int, wis: Int, cha: Int) {
        _creationDraft.value = _creationDraft.value.copy(
            strength     = str,
            dexterity    = dex,
            constitution = con,
            intelligence = int,
            wisdom       = wis,
            charisma     = cha,
        )
    }

    fun updateDraftDetails(name: String, background: String, alignment: String, level: Int, backstory: String) {
        _creationDraft.value = _creationDraft.value.copy(
            name       = name,
            background = background,
            alignment  = alignment,
            level      = level,
            backstory  = backstory,
        )
    }

    fun updateDraftPortraitColors(primary: Long, secondary: Long) {
        _creationDraft.value = _creationDraft.value.copy(
            portraitPrimaryColor = primary,
            portraitSecondaryColor = secondary,
        )
    }

    fun nextStep() {
        val next = CreationStep.entries.getOrNull(_creationStep.value.ordinal + 1)
        if (next != null) _creationStep.value = next
    }

    fun previousStep() {
        val prev = CreationStep.entries.getOrNull(_creationStep.value.ordinal - 1)
        if (prev != null) _creationStep.value = prev
    }

    fun resetWizard() {
        _creationStep.value = CreationStep.RACE
        _creationDraft.value = Character()
    }

    fun createCharacter(onSuccess: (String) -> Unit, onError: (String) -> Unit) {
        val userId = authRepository.currentUserId() ?: return onError("Not logged in")
        if (_isCreating.value) return
        _isCreating.value = true

        viewModelScope.launch {
            try {
                val profile = userRepository.getUserProfile(userId)
                val count   = characterRepo.getCharacterCount(userId)

                if (count >= 3 && profile?.tier == "free") {
                    _characterListState.value = CharacterListState.ShowUpgradePrompt
                    onError("Character limit reached for free tier")
                    return@launch
                }

                val draft   = _creationDraft.value
                val hitDieName = CharacterCalculator.CLASS_HIT_DICE[draft.characterClass] ?: "d8"
                val hitDieSize = hitDieName.removePrefix("d").toIntOrNull() ?: 8
                val conMod  = CharacterCalculator.modifier(draft.constitution)
                val dexMod  = CharacterCalculator.modifier(draft.dexterity)
                val maxHp   = maxOf(1, hitDieSize + conMod)

                val finalized = draft.copy(
                    userId            = userId,
                    maxHp             = maxHp,
                    currentHp         = maxHp,
                    armorClass        = 10 + dexMod,
                    initiative        = dexMod,
                    hitDice           = "1$hitDieName",
                    hitDiceRemaining  = draft.level,
                    spellSlots        = CharacterCalculator.spellSlotsForClass(draft.characterClass, draft.level),
                )

                val id = characterRepo.createCharacter(finalized)
                resetWizard()
                onSuccess(id)
            } catch (e: Exception) {
                onError(e.message ?: "Failed to create character")
            } finally {
                _isCreating.value = false
            }
        }
    }

    // ── Sheet actions ────────────────────────────────────────────────────────

    fun adjustHp(delta: Int) {
        val char   = _selectedCharacter.value ?: return
        val userId = authRepository.currentUserId() ?: return
        val newHp  = (char.currentHp + delta).coerceIn(0, char.maxHp)
        _selectedCharacter.value = char.copy(currentHp = newHp)
        viewModelScope.launch {
            characterRepo.updateHp(userId, char.id, newHp, char.temporaryHp)
        }
    }

    fun setHp(currentHp: Int, temporaryHp: Int) {
        val char   = _selectedCharacter.value ?: return
        val userId = authRepository.currentUserId() ?: return
        val newHp  = currentHp.coerceIn(0, char.maxHp)
        val newTmp = temporaryHp.coerceAtLeast(0)
        _selectedCharacter.value = char.copy(currentHp = newHp, temporaryHp = newTmp)
        viewModelScope.launch {
            characterRepo.updateHp(userId, char.id, newHp, newTmp)
        }
    }

    fun toggleCondition(conditionName: String) {
        val char   = _selectedCharacter.value ?: return
        val userId = authRepository.currentUserId() ?: return
        val conditions = if (conditionName in char.conditions)
            char.conditions - conditionName
        else
            char.conditions + conditionName
        _selectedCharacter.value = char.copy(conditions = conditions)
        viewModelScope.launch {
            characterRepo.updateConditions(userId, char.id, conditions)
        }
    }

    fun toggleSkillProficiency(skill: String) {
        val char = _selectedCharacter.value ?: return
        val newProfs = if (skill in char.skillProficiencies)
            char.skillProficiencies - skill
        else
            char.skillProficiencies + skill
        val updated = char.copy(skillProficiencies = newProfs)
        _selectedCharacter.value = updated
        viewModelScope.launch { characterRepo.updateCharacter(updated) }
    }

    fun toggleSavingThrowProficiency(ability: String) {
        val char = _selectedCharacter.value ?: return
        val newProfs = if (ability in char.savingThrowProficiencies)
            char.savingThrowProficiencies - ability
        else
            char.savingThrowProficiencies + ability
        val updated = char.copy(savingThrowProficiencies = newProfs)
        _selectedCharacter.value = updated
        viewModelScope.launch { characterRepo.updateCharacter(updated) }
    }

    fun toggleSpellSlotUsed(slotLevel: String, slotIndex: Int) {
        val char   = _selectedCharacter.value ?: return
        val userId = authRepository.currentUserId() ?: return
        val slot   = char.spellSlots[slotLevel] ?: return
        val newUsed = if (slotIndex < slot.used) slot.used - 1 else slot.used + 1
        val newSlots = char.spellSlots + (slotLevel to slot.copy(used = newUsed.coerceIn(0, slot.total)))
        _selectedCharacter.value = char.copy(spellSlots = newSlots)
        viewModelScope.launch {
            characterRepo.updateSpellSlots(userId, char.id, newSlots)
        }
    }

    fun addInventoryItem(item: InventoryItem) {
        val char    = _selectedCharacter.value ?: return
        val updated = char.copy(inventory = char.inventory + item)
        _selectedCharacter.value = updated
        viewModelScope.launch { characterRepo.updateCharacter(updated) }
    }

    fun removeInventoryItem(itemId: String) {
        val char    = _selectedCharacter.value ?: return
        val updated = char.copy(inventory = char.inventory.filter { it.id != itemId })
        _selectedCharacter.value = updated
        viewModelScope.launch { characterRepo.updateCharacter(updated) }
    }

    fun saveNotes(notes: String) {
        val char    = _selectedCharacter.value ?: return
        val updated = char.copy(notes = notes)
        _selectedCharacter.value = updated
        viewModelScope.launch { characterRepo.updateCharacter(updated) }
    }

    // ── Spell management ─────────────────────────────────────────────────────
    // Delegates to updateCharacter (proven full-doc write) to avoid encoding
    // issues with partial Firestore field updates.

    private val _spellError = MutableSharedFlow<String>(extraBufferCapacity = 1)
    val spellError: SharedFlow<String> = _spellError.asSharedFlow()

    fun addSpell(spellSlug: String) {
        val char = _selectedCharacter.value ?: return
        if (spellSlug in char.knownSpells) return
        val updated = char.copy(knownSpells = char.knownSpells + spellSlug)
        _selectedCharacter.value = updated
        viewModelScope.launch {
            try {
                characterRepo.updateCharacter(updated)
            } catch (e: Exception) {
                _selectedCharacter.value = char
                _spellError.tryEmit("Failed to add spell: ${e.message}")
            }
        }
    }

    fun removeSpell(spellSlug: String) {
        val char = _selectedCharacter.value ?: return
        val updated = char.copy(
            knownSpells    = char.knownSpells - spellSlug,
            preparedSpells = char.preparedSpells - spellSlug,
        )
        _selectedCharacter.value = updated
        viewModelScope.launch {
            try {
                characterRepo.updateCharacter(updated)
            } catch (e: Exception) {
                _selectedCharacter.value = char
                _spellError.tryEmit("Failed to remove spell: ${e.message}")
            }
        }
    }

    fun togglePrepared(spellSlug: String) {
        val char = _selectedCharacter.value ?: return
        val newPrepared = if (spellSlug in char.preparedSpells)
            char.preparedSpells - spellSlug
        else
            char.preparedSpells + spellSlug
        val updated = char.copy(preparedSpells = newPrepared)
        _selectedCharacter.value = updated
        viewModelScope.launch {
            try {
                characterRepo.updateCharacter(updated)
            } catch (e: Exception) {
                _selectedCharacter.value = char
                _spellError.tryEmit("Failed to update prepared: ${e.message}")
            }
        }
    }

    fun updatePortraitColors(primary: Long, secondary: Long) {
        val char = _selectedCharacter.value ?: return
        val updated = char.copy(portraitPrimaryColor = primary, portraitSecondaryColor = secondary)
        _selectedCharacter.value = updated
        viewModelScope.launch { characterRepo.updateCharacter(updated) }
    }

    fun deleteCharacter(onDone: () -> Unit) {
        val char   = _selectedCharacter.value ?: return
        val userId = authRepository.currentUserId() ?: return
        viewModelScope.launch {
            characterRepo.deleteCharacter(userId, char.id)
            _selectedCharacter.value = null
            onDone()
        }
    }
}
